/** bcrypt cost used when hashing local passwords. */
export const BCRYPT_SALT_ROUNDS = 10;

/** Redis keys used to store password reset tokens and related limits. */
export const PASSWORD_RESET_CACHE_KEYS = {
  RATE_EMAIL: 'password-reset:rate-email:{email}',
  RATE_IP: 'password-reset:rate-ip:{ip}',
  SESSION_INVALIDATED_AT: 'auth:session-invalidated-at:{userId}',
  TOKEN: 'password-reset:token:{tokenHash}',
  USER: 'password-reset:user:{userId}',
} as const;

/** Max forgot-password emails per address in the rate-limit window. */
export const PASSWORD_RESET_EMAIL_RATE_LIMIT_MAX = 3;

/** Window for the per-email forgot-password rate limit. */
export const PASSWORD_RESET_EMAIL_RATE_LIMIT_WINDOW_SECONDS = 15 * 60;

/** Max forgot-password requests per IP in the rate-limit window. */
export const PASSWORD_RESET_IP_RATE_LIMIT_MAX = 20;

/** Window for the per-IP forgot-password rate limit. */
export const PASSWORD_RESET_IP_RATE_LIMIT_WINDOW_SECONDS = 15 * 60;

/** How long a session-invalidation marker outlives an Express session. */
export const AUTH_SESSION_INVALIDATED_AT_TTL_SECONDS = 2 * 60 * 60;

/** Campus apps on production (`app.didaskill.fr`) or an environment (`app.develop.didaskill.fr`). */
export const DIDASKILL_CAMPUS_HOSTNAME_PATTERN =
  /^(?:app|bo|tuteur)(?:\.[a-z0-9-]+)?\.didaskill\.fr$/i;

/** Value of `app.env` (`ENVIRONMENT`) for local development. */
export const LOCAL_APP_ENVIRONMENT = 'local';

/** Hostnames allowed in password-reset links only when `app.env` is local. */
export const LOCAL_DEVELOPMENT_HOSTNAMES = new Set(['localhost', '127.0.0.1']);

export enum PASSWORD_RESET_ERRORS {
  INVALID_TOKEN = 'INVALID_PASSWORD_RESET_TOKEN',
}

export enum PASSWORD_RESET_PURPOSE {
  RESET = 'reset',
  SET = 'set',
}

/** Entropy of the opaque password-reset token, in bytes. */
export const PASSWORD_RESET_TOKEN_BYTES = 32;

/** Lifetime of a password-reset token. */
export const PASSWORD_RESET_TOKEN_TTL_SECONDS = 15 * 60;

/** Public web path that requests a password-reset email. */
export const LOCAL_FORGOT_PASSWORD_PATH = '/forgot-password';

/** Public web path of the campus login page. */
export const LOCAL_LOGIN_PATH = '/login';

/** Public web path that consumes a password-reset token. */
export const LOCAL_PASSWORD_RESET_PATH = '/reset-password';

/** Named character-class checks a local password must satisfy, in addition to length. */
export const PASSWORD_COMPLEXITY_PATTERNS = {
  DIGIT: { name: 'digit', regex: /[0-9]/ },
  LOWERCASE: { name: 'lowercase', regex: /[a-z]/ },
  SYMBOL: { name: 'symbol', regex: /[^A-Za-z0-9]/ },
  UPPERCASE: { name: 'uppercase', regex: /[A-Z]/ },
} as const;

/** Maximum password length accepted by bcrypt. */
export const PASSWORD_MAX_LENGTH = 72;

/** Minimum password length for local authentication. */
export const PASSWORD_MIN_LENGTH = 8;
